// ============================================================================
// handleOpenClaw — Drop-in thunk similar to handleAgentIQ
//
// Usage:
//   dispatch(handleOpenClaw("What's the weather like?"));
//   dispatch(handleOpenClaw("Analyze this dataset", true)); // re-init connection
//
// On first call (or when shouldInit=true), it establishes the WebSocket
// connection to the OpenClaw Gateway. Subsequent calls just send messages.
// ============================================================================
import { connectToOpenClaw, disconnectFromOpenClaw, sendOpenClawMessage } from "./openClawThunk";
import { selectIsOpenClawConnected, selectOpenClawConnectionStatus } from "./openClawSlice";
import { addMessageConversation } from "containers/ChatGPT/reducer";
// ---------------------------------------------------------------------------
// Main thunk
// ---------------------------------------------------------------------------
/**
 * Send a prompt to the OpenClaw Gateway.
 *
 * Reads the gateway URL and token from Redux state (set by the user via
 * the config inputs, persisted to localStorage).
 *
 * @param prompt      - The user's message text
 * @param shouldInit  - Force (re)connect to the Gateway (default: auto-detect)
 */
export function handleOpenClaw(prompt, shouldInit = false, selectedSandbox, hideUserMessage = false) {
    return async (dispatch, getState) => {
        const isConnected = selectIsOpenClawConnected(getState());
        // ------------------------------------------------------------------
        // 1. Connect if needed
        // ------------------------------------------------------------------
        if (shouldInit || !isConnected) {
            // If we're re-initializing, tear down the old connection
            if (shouldInit && isConnected) {
                dispatch(disconnectFromOpenClaw());
            }
            // Updating this to not use openClaw state as the fallback for selectedSandbox to prevent connecting to the wrong sandbox
            // const url = selectedSandbox?.gateway_url ? selectedSandbox?.gateway_url : selectOpenClawGatewayUrl(getState());
            // const token = selectedSandbox?.gateway_token
            //   ? selectedSandbox?.gateway_token
            //   : selectOpenClawGatewayToken(getState());
            const url = selectedSandbox?.gateway_url;
            const token = selectedSandbox?.gateway_token;
            if (!url) {
                dispatch(addMessageConversation({
                    user: "Aible",
                    text: "OpenClaw Gateway URL error, please try loading the sandbox again.",
                    date: new Date()
                }));
                return;
            }
            if (!token) {
                dispatch(addMessageConversation({
                    user: "Aible",
                    text: "OpenClaw Gateway token error, please try loading the sandbox again.",
                    date: new Date()
                }));
                return;
            }
            // Connect and wait for it to be ready
            dispatch(connectToOpenClaw(url, token));
            // Wait for connection (up to 10s)
            const connected = await waitForConnection(getState, 10_000);
            if (!connected) {
                dispatch(addMessageConversation({
                    user: "Aible",
                    text: "Could not connect to OpenClaw Gateway. Please verify the gateway is running and your URL/token are correct.",
                    date: new Date()
                }));
                return;
            }
        }
        // ------------------------------------------------------------------
        // 2. Send the message
        // ------------------------------------------------------------------
        dispatch(sendOpenClawMessage(prompt, hideUserMessage));
    };
}
// ---------------------------------------------------------------------------
// Helper: wait for the WebSocket connection to establish
// ---------------------------------------------------------------------------
function waitForConnection(getState, timeoutMs) {
    return new Promise((resolve) => {
        const start = Date.now();
        const check = () => {
            if (selectIsOpenClawConnected(getState())) {
                resolve(true);
                return;
            }
            const status = selectOpenClawConnectionStatus(getState());
            if (status === "error") {
                resolve(false);
                return;
            }
            if (Date.now() - start > timeoutMs) {
                resolve(false);
                return;
            }
            setTimeout(check, 100);
        };
        check();
    });
}
